import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ContractingCompaniesRepository } from "@/api/contracting-companies/repository";
import { ContractingCompaniesService } from "@/api/contracting-companies/service";
import { LendersRepository } from "@/api/lenders/repository";
import { LendersService } from "@/api/lenders/service";
import type { ISortOrder } from "@/types/common";
import type { ISelectOption } from "@/ui-kit";
import { Avatar, ComboBox, FormField } from "@/ui-kit";

type IProps = {
  name: string;
  company: "contractor" | "lender";
  onChange?: (option: ISelectOption | undefined) => void;
  onClear?: () => void;
  disabled?: boolean;
};

type IFilters = Partial<{
  statusFilter: string;
  searchQuery: string;
  page: string;
  size: string;
  sortField: string;
  sortOrder: ISortOrder;
}>;

export const CompanyControl = ({
  name,
  company,
  onChange,
  onClear,
  disabled,
}: IProps) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const { errors } = useFormState();

  const defaultOptions = useRef<ISelectOption[] | null>(null);
  const [options, setOptions] = useState<ISelectOption[]>([]);
  const [localValue, setLocalValue] = useState<ISelectOption | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const hasMore = useRef<boolean>(true);

  const [filters, setFilters] = useState<IFilters>({
    statusFilter: "",
    searchQuery: "",
    page: "1",
    size: "20",
    sortField: "name",
    sortOrder: "asc",
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const repository = useMemo(() => {
    const map = {
      contractor: new ContractingCompaniesRepository(
        new ContractingCompaniesService(),
        { abort: true },
      ),
      lender: new LendersRepository(new LendersService(), { abort: true }),
    };
    return map[company];
  }, [company]);

  const handleSearch = useCallback(
    (filters: IFilters): Promise<ISelectOption[]> => {
      if (!repository) {
        return Promise.resolve([]);
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      return repository
        .getAll(filters, {
          signal: abortControllerRef.current.signal,
        })
        .then((response) => {
          if (response.page === response.totalPages) {
            hasMore.current = false;
          }

          return response.content.map((item) => {
            return {
              id: item.id.toString(),
              text: item.name,
              icon: (
                <Avatar size="xs" src={item.companyLogo} showDetails={false} />
              ),
              data: item,
            };
          });
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.error("Failed to fetch companies:", error);
          }
          throw error;
        })
        .finally(() => {
          if (abortControllerRef.current) {
            abortControllerRef.current = null;
          }
        });
    },
    [repository],
  );

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (
      defaultOptions.current &&
      filters.searchQuery === "" &&
      filters.page === "1"
    ) {
      setOptions(defaultOptions.current);
      return;
    }

    if (filters.page !== "1") {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    handleSearch(filters)
      .then((data) => {
        if (!defaultOptions.current) {
          defaultOptions.current = data;
        }
        if (filters.page !== "1") {
          setLoadingMore(false);
          setOptions((prev) => [...prev, ...data]);
        } else {
          setLoading(false);
          setOptions(data);
        }
      })
      .catch(() => {
        setLoadingMore(false);
        setLoading(false);
      });
  }, [filters, handleSearch]);

  const onSearch = useCallback((query: string) => {
    hasMore.current = true;
    setFilters((prev: IFilters): IFilters => {
      return {
        ...prev,
        searchQuery: query,
        page: "1",
      };
    });
  }, []);

  const onLoad = useCallback(() => {
    if (!hasMore.current || loadingMore) {
      return;
    }
    setFilters((prev: IFilters): IFilters => {
      return {
        ...prev,
        page: `${+(prev.page || "1") + 1}`,
      };
    });
  }, [loadingMore]);

  const handleChange = useCallback(
    (option: ISelectOption | ISelectOption[] | undefined) => {
      const o = option as ISelectOption | undefined;
      if (o) {
        setLocalValue(o);
        if (onChange) {
          onChange(o);
        } else {
          setValue(name, o.id);
        }
      } else {
        setLocalValue(undefined);
        if (onChange) {
          onChange(undefined);
        } else {
          setValue(name, []);
        }
      }
    },
    [name, onChange, setValue],
  );

  const handleClear = useCallback(() => {
    hasMore.current = true;
    setLocalValue(undefined);
    if (onClear) {
      onClear();
    } else {
      setValue(name, []);
    }
    setFilters((prev: IFilters): IFilters => {
      return {
        ...prev,
        searchQuery: "",
        page: "1",
      };
    });
  }, [name, onClear, setValue]);

  // The FormField label names the ComboBox input through `<label for>`.
  const inputId = useId();

  return (
    <FormField
      htmlFor={inputId}
      label={t("label.company")}
      message={errors?.[name]?.message as string}
    >
      <ComboBox
        inputId={inputId}
        options={options}
        value={localValue}
        onChange={handleChange}
        onClear={handleClear}
        onSearch={onSearch}
        onLoad={onLoad}
        loading={loading}
        loadingMore={loadingMore}
        placeholder={t("placeholder.selectCompany")}
        invalid={!!errors?.[name]}
        disabled={disabled}
        maxHeight={240}
        openLength={0}
      />
    </FormField>
  );
};
